import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nom || !formData.email || !formData.telephone || !formData.message) {
      setSubmitMessage('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5nanev72m0gvhnvdvgg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.');
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          sujet: '',
          message: ''
        });
      } else {
        setSubmitMessage('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (error) {
      setSubmitMessage('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-black via-[#2D3142] to-black">
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block bg-[#E63946]/20 text-[#E63946] px-6 py-2 rounded-full text-sm font-semibold mb-6">
            CONTACTEZ-NOUS
          </span>
          <h1 className="text-white text-6xl lg:text-7xl font-black mb-6">
            Parlons de<br />
            <span className="text-[#E63946]">Votre Projet</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto leading-relaxed">
            Notre équipe est à votre écoute pour répondre à toutes vos questions et vous accompagner dans vos projets d'impression
          </p>
        </div>
      </section>

      {/* Contact Info & Form Section */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* Contact Cards */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#E63946] to-[#d32f3c] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-phone-line text-white text-3xl"></i>
              </div>
              <h3 className="text-black text-xl font-bold mb-3">Téléphone</h3>
              <a
                href="tel:+2250779027536"
                className="text-gray-600 text-base hover:text-[#E63946] transition-colors cursor-pointer"
              >
                +225 0779027536
              </a>
              <p className="text-gray-500 text-sm mt-2">Lun - Sam: 8h - 18h</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#E63946] to-[#d32f3c] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-whatsapp-line text-white text-3xl"></i>
              </div>
              <h3 className="text-black text-xl font-bold mb-3">WhatsApp</h3>
              <a
                href="https://wa.me/2250779027536"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 text-base hover:text-[#E63946] transition-colors cursor-pointer"
              >
                +225 0779027536
              </a>
              <p className="text-gray-500 text-sm mt-2">Réponse rapide 24/7</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#E63946] to-[#d32f3c] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-line text-white text-3xl"></i>
              </div>
              <h3 className="text-black text-xl font-bold mb-3">Email</h3>
              <a
                href="mailto:contact@grandeimprimerie.com"
                className="text-gray-600 text-base hover:text-[#E63946] transition-colors cursor-pointer break-all"
              >
                contact@grandeimprimerie.com
              </a>
              <p className="text-gray-500 text-sm mt-2">Réponse sous 24h</p>
            </div>
          </div>

          {/* Form */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
              <div className="text-center mb-10">
                <h2 className="text-black text-4xl font-bold mb-4">
                  Envoyez-nous un Message
                </h2>
                <p className="text-gray-600 text-base">
                  Remplissez le formulaire ci-dessous et nous vous répondrons rapidement
                </p>
              </div>

              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="nom" className="block text-gray-800 text-sm font-semibold mb-2">
                      Nom complet <span className="text-[#E63946]">*</span>
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/20 outline-none transition-all text-sm"
                      placeholder="Votre nom complet"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-gray-800 text-sm font-semibold mb-2">
                      Email <span className="text-[#E63946]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/20 outline-none transition-all text-sm"
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="telephone" className="block text-gray-800 text-sm font-semibold mb-2">
                      Téléphone <span className="text-[#E63946]">*</span>
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/20 outline-none transition-all text-sm"
                      placeholder="+243 xxx xxx xxx"
                    />
                  </div>

                  <div>
                    <label htmlFor="sujet" className="block text-gray-800 text-sm font-semibold mb-2">
                      Sujet
                    </label>
                    <select
                      id="sujet"
                      name="sujet"
                      value={formData.sujet}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/20 outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">Sélectionnez un sujet</option>
                      <option value="Demande de devis">Demande de devis</option>
                      <option value="Informations services">Informations sur les services</option>
                      <option value="Suivi de commande">Suivi de commande</option>
                      <option value="Réclamation">Réclamation</option>
                      <option value="Partenariat">Partenariat</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="message" className="block text-gray-800 text-sm font-semibold mb-2">
                      Votre message <span className="text-[#E63946]">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/20 outline-none transition-all resize-none text-sm"
                      placeholder="Décrivez votre besoin ou posez votre question..."
                    ></textarea>
                    <p className="text-gray-500 text-xs mt-1">Maximum 500 caractères</p>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#E63946] text-white px-12 py-4 rounded-full font-bold text-base shadow-2xl hover:bg-[#d32f3c] transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill"></i>
                        Envoyer le message
                      </>
                    )}
                  </button>
                </div>

                {submitMessage && (
                  <div className={`mt-6 p-4 rounded-lg text-center ${
                    submitMessage.includes('succès') 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    <p className="text-sm font-medium">{submitMessage}</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-black text-4xl font-bold mb-4">
              Notre Localisation
            </h2>
            <p className="text-gray-600 text-lg">
              Visitez-nous dans nos locaux
            </p>
          </div>

          <div className="rounded-3xl overflow-hidden shadow-2xl h-[500px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3978.4901234567!2d15.3!3d-4.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNMKwMTgnMDAuMCJTIDE1wrAxOCcwMC4wIkU!5e0!3m2!1sfr!2scd!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localisation Grande Imprimerie"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-black text-4xl font-bold mb-4">
              Actions Rapides
            </h2>
            <p className="text-gray-600 text-lg">
              Choisissez la meilleure façon de nous contacter
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <a
              href="tel:+2250779027536"
              className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group cursor-pointer"
            >
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E63946] transition-colors">
                <i className="ri-phone-line text-white text-3xl"></i>
              </div>
              <h3 className="text-black text-xl font-bold mb-3">Appelez-nous</h3>
              <p className="text-gray-600 text-base mb-4">
                Pour une réponse immédiate
              </p>
              <span className="text-[#E63946] font-bold">+225 0779027536</span>
            </a>

            <a
              href="https://wa.me/2250779027536"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group cursor-pointer"
            >
              <div className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-whatsapp-line text-white text-3xl"></i>
              </div>
              <h3 className="text-black text-xl font-bold mb-3">WhatsApp</h3>
              <p className="text-gray-600 text-base mb-4">
                Chat en temps réel
              </p>
              <span className="text-[#25D366] font-bold">Discuter maintenant</span>
            </a>

            <a
              href="mailto:contact@grandeimprimerie.com"
              className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group cursor-pointer"
            >
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E63946] transition-colors">
                <i className="ri-mail-line text-white text-3xl"></i>
              </div>
              <h3 className="text-black text-xl font-bold mb-3">Email</h3>
              <p className="text-gray-600 text-base mb-4">
                Écrivez-nous par email
              </p>
              <span className="text-[#E63946] font-bold">Envoyer un email</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;